/**
 * Slice-① landing command set — a framework-free factory for the ⌘K palette's
 * registry (no React/DOM), so the command shape can be unit tested without a
 * renderer (mirrors `command.matcher.ts`). The palette wiring
 * (`__command-palette.tsx`) injects `onTheme`, which carries the toast + cookie
 * side effects; this module only declares *which* commands exist.
 */

import { type TFunction } from 'i18next'
import { type Command } from '#app/components/ui/command.matcher.ts'
import { type Theme } from '#app/utils/theme.server.ts'
import { navSections } from './__sections.ts'
import { type IconName } from '@/icon-name'

/** The theme *preference* a command can set — the resolved `Theme` plus `system`. */
export type ThemeMode = Theme | 'system'

/** The translator the palette copy resolves against — the landing `public` copy
 * plus the shared `common.nav` section/blog labels (ADR-074). */
export type LandingT = TFunction<['public', 'common']>

/** Leading icon per nav target; falls back to a generic arrow for new sections. */
const navIcons: Partial<Record<(typeof navSections)[number]['id'], IconName>> = {
	work: 'link-2',
	services: 'file-text',
	pricing: 'file-text',
	faq: 'question-mark-circled',
}

/** The 3-way theme set, mirroring the customizer dock's segment (ADR 062). */
const themeModes: Array<{ mode: ThemeMode; icon: IconName }> = [
	{ mode: 'light', icon: 'sun' },
	{ mode: 'dark', icon: 'moon' },
	{ mode: 'system', icon: 'laptop' },
]

/**
 * Build the landing palette registry: a Navigation group of `href` commands
 * (Home, each in-page nav section, Contact) and a Theme group of `run` commands
 * that report the chosen mode to `onTheme`. The `run` XOR `href` split keeps each
 * command unambiguously a navigation or an action (enforced by {@link Command}).
 * Every visible title/group is localized through `t` (ADR-074).
 */
export function landingCommands({
	onTheme,
	t,
}: {
	onTheme: (mode: ThemeMode) => void
	t: LandingT
}): Command[] {
	const navigation: Command[] = [
		{
			id: 'nav-home',
			title: t('landing.command.commands.home'),
			group: t('landing.command.groups.navigation'),
			href: '/',
			icon: 'arrow-right',
		},
		{
			id: 'nav-blog',
			title: t('common:nav.blog'),
			group: t('landing.command.groups.navigation'),
			href: '/blog',
			icon: 'file-text',
			keywords: ['articles', 'posts', 'writing'],
		},
		...navSections.map(
			(section): Command => ({
				id: `nav-${section.id}`,
				title: t(`common:nav.sections.${section.id}`),
				group: t('landing.command.groups.navigation'),
				href: `/#${section.id}`,
				icon: navIcons[section.id] ?? 'arrow-right',
			}),
		),
		{
			id: 'nav-contact',
			title: t('landing.command.commands.contact'),
			group: t('landing.command.groups.navigation'),
			href: '/#contact',
			icon: 'envelope-closed',
			keywords: ['contact', 'hire', 'get in touch'],
		},
	]

	const theme: Command[] = themeModes.map(({ mode, icon }) => ({
		id: `theme-${mode}`,
		title: t(`landing.command.theme.${mode}`),
		group: t('landing.command.groups.theme'),
		icon,
		keywords: ['theme', 'appearance', 'dark', 'light'],
		run: () => onTheme(mode),
	}))

	return [...navigation, ...theme]
}
